import React, { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconChevronDown, IconClock } from '@tabler/icons-react';
import { DatePicker } from 'antd';
import { CButton } from '@app/components/Common';
import { COLORS } from '@app/constants';
import useTradeStore, { useZTradeStore } from '@app/hooks/trade/tradeStore';
import { View, Text } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import dayjs, { Dayjs } from 'dayjs';
import styled, { createGlobalStyle } from 'styled-components';

// Time In Force 타입 정의
type TimeInForceType = 'GTC' | 'GTD' | 'IOC' | 'FOK';

interface TimeInForceOption {
  value: TimeInForceType;
  label: string;
  labelKey: string;
  disabled: boolean;
}

const TimeInForceDropdown: React.FC = () => {
  const { textsilver, gray, textw, drawerBgColor, PrimaryBorder } =
    useContext(ThemeContext).theme.COLORS;
  const [display, setDisplay] = useState<boolean>(false);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [tempExpiry, setTempExpiry] = useState<Dayjs | null>(null); // 임시 만료 시간
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: tradeStore, mutate: mutateTradeStore } = useTradeStore();

  // 지정가 주문일 때만 표시
  const isLimitOrder = tradeStore?.limit_market === 'L';

  // Time In Force 옵션 정의
  const options: TimeInForceOption[] = [
    { value: 'GTC', label: 'GTC', labelKey: 'tifGtc', disabled: false },
    { value: 'GTD', label: 'GTD', labelKey: 'tifGtd', disabled: false },
    { value: 'IOC', label: 'IOC', labelKey: 'tifIoc', disabled: true },
    { value: 'FOK', label: 'FOK', labelKey: 'tifFok', disabled: true },
  ];

  // 현재 선택된 Time In Force (기본값: GTC)
  const currentTif: TimeInForceType = tradeStore?.time_in_force || 'GTC';
  const currentOption = options.find((opt) => opt.value === currentTif);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // DatePicker가 열려있으면 드롭다운 닫지 않음
      if (isDatePickerOpen) return;

      // DatePicker 팝업 클릭인지 확인
      const target = event.target as HTMLElement;
      if (target.closest('.ant-picker-dropdown')) return;

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDisplay(false);
      }
    };

    if (display) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [display, isDatePickerOpen]);

  // Time In Force 변경 핸들러
  const handleTifChange = (value: TimeInForceType) => {
    const currentState = useZTradeStore.getState().data;
    const updateData: any = {
      ...currentState,
      time_in_force: value,
    };

    // GTC 선택 시 gtd_expiry 초기화
    if (value !== 'GTD') {
      updateData.gtd_expiry = null;
      setTempExpiry(null);
      setDisplay(false);
      setShowDatePicker(false);
    } else {
      // GTD 선택 시 임시 만료 시간 설정 (기존 값 또는 24시간 후)
      const existingExpiry = currentState?.gtd_expiry;
      const initialExpiry = existingExpiry
        ? dayjs(existingExpiry)
        : dayjs().add(24, 'hour');
      setTempExpiry(initialExpiry);

      // 기존에 저장된 값이 없으면 기본값 저장
      if (!existingExpiry) {
        updateData.gtd_expiry = initialExpiry.toISOString();
      }
      setShowDatePicker(true);
    }

    mutateTradeStore(updateData);
  };

  // DatePicker에서 날짜/시간 선택 중 (임시 상태 업데이트)
  const handleCalendarChange = (date: Dayjs | Dayjs[] | null) => {
    const singleDate = Array.isArray(date) ? date[0] : date;
    if (singleDate) {
      setTempExpiry(singleDate);
    }
  };

  // OK 버튼 클릭 시 최종 저장 (tempExpiry 사용)
  const handleOk = () => {
    if (tempExpiry) {
      const currentState = useZTradeStore.getState().data;
      mutateTradeStore({
        ...currentState,
        time_in_force: 'GTD',
        gtd_expiry: tempExpiry.toISOString(),
      });
      setDisplay(false);
      setShowDatePicker(false);
    }
  };

  // GTD 만료 시간 제한 (현재 ~ 30일 후)
  const disabledDate = (current: Dayjs) => {
    const now = dayjs();
    const maxDate = dayjs().add(30, 'day');
    return current && (current < now.startOf('day') || current > maxDate);
  };

  // 현재 GTD 만료 시간 (tempExpiry 우선, 없으면 tradeStore에서)
  const gtdExpiry =
    tempExpiry ||
    (tradeStore?.gtd_expiry
      ? dayjs(tradeStore.gtd_expiry)
      : dayjs().add(24, 'hour'));

  // 표시될 라벨 생성
  const getDisplayLabel = () => {
    if (currentTif === 'GTD' && tradeStore?.gtd_expiry) {
      return `GTD ${dayjs(tradeStore.gtd_expiry).format('MM/DD HH:mm')}`;
    }
    return currentOption?.label || 'GTC';
  };

  if (!isLimitOrder) {
    return null;
  }

  return (
    <View style={{ position: 'relative' }} ref={dropdownRef}>
      <CButton
        icon={<IconChevronDown size={12} style={{ marginLeft: 4 }} />}
        title={getDisplayLabel()}
        onClick={() => {
          const newDisplay = !display;
          setDisplay(newDisplay);
          // 드롭다운을 열 때 GTD가 선택되어 있으면 DatePicker 자동 표시
          if (newDisplay && currentTif === 'GTD') {
            setShowDatePicker(true);
          }
        }}
        style={{
          color: COLORS.lightGray,
          fontSize: 12,
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          gap: 2,
          cursor: 'pointer',
          fontFamily: 'NanumSquareRound',
        }}
      />

      {display && (
        <DropdownContainer
          $bgColor={drawerBgColor}
          $borderColor={PrimaryBorder}
        >
          {/* 옵션 목록 */}
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              $active={currentTif === option.value}
              $disabled={option.disabled}
              onClick={() => {
                if (!option.disabled) {
                  handleTifChange(option.value);
                }
              }}
            >
              <Text
                style={{
                  fontSize: 12,
                  color: option.disabled
                    ? COLORS.lightGray5
                    : currentTif === option.value
                      ? '#07D993'
                      : textw,
                }}
              >
                {option.label}
              </Text>
              <Text
                style={{
                  fontSize: 11,
                  color: option.disabled ? COLORS.lightGray5 : textsilver,
                  marginLeft: 8,
                }}
              >
                {t(option.labelKey)}
                {option.disabled && ` (${t('tifNotSupported')})`}
              </Text>
            </DropdownItem>
          ))}

          {/* GTD 선택 시 날짜 선택기 */}
          {showDatePicker && currentTif === 'GTD' && (
            <DatePickerWrapper>
              <Text
                style={{
                  fontSize: 11,
                  color: textsilver,
                  marginBottom: 8,
                }}
              >
                {t('selectExpiry')} ({t('max30Days')})
              </Text>
              <DatePickerGlobalStyle />
              <StyledDatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                value={gtdExpiry}
                onCalendarChange={handleCalendarChange}
                onOk={handleOk}
                onOpenChange={(open) => {
                  setIsDatePickerOpen(open);
                  // DatePicker 열릴 때 현재 저장된 값으로 tempExpiry 초기화
                  if (open) {
                    const currentState = useZTradeStore.getState().data;
                    const existingExpiry = currentState?.gtd_expiry;
                    setTempExpiry(
                      existingExpiry
                        ? dayjs(existingExpiry)
                        : dayjs().add(24, 'hour')
                    );
                  }
                }}
                disabledDate={disabledDate}
                placeholder={t('selectExpiry')}
                allowClear={false}
                size="small"
                placement="topLeft"
                popupClassName="tif-datepicker-popup"
              />
            </DatePickerWrapper>
          )}
        </DropdownContainer>
      )}
    </View>
  );
};

const DropdownContainer = styled.div<{
  $bgColor: string;
  $borderColor: string;
}>`
  position: absolute;
  width: 220px;
  background: ${(props) => props.$bgColor};
  right: 0;
  top: 100%;
  margin-top: 4px;
  padding: 8px 0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid ${(props) => props.$borderColor};
  z-index: 1000;
`;

const DropdownItem = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  background: ${(props) =>
    props.$active ? 'rgba(138, 235, 195, 0.1)' : 'transparent'};

  &:hover {
    background: ${(props) =>
      props.$disabled ? 'transparent' : 'rgba(7, 217, 147, 0.05)'};
  }
`;

const DatePickerWrapper = styled.div`
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 8px;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;

  .ant-picker-input > input {
    color: white !important;
    font-size: 12px;
  }
`;

// DatePicker 팝업 스타일 (포탈로 body에 렌더링되므로 GlobalStyle 필요)
const DatePickerGlobalStyle = createGlobalStyle`
  .tif-datepicker-popup {
    .ant-picker-panel-container {
      background: #1a1a1a;
    }

    .ant-picker-header {
      color: #fff;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .ant-picker-header button {
      color: #fff;
    }

    .ant-picker-content th {
      color: rgba(255, 255, 255, 0.6);
    }

    .ant-picker-cell {
      color: rgba(255, 255, 255, 0.85);
    }

    .ant-picker-cell-disabled {
      color: rgba(255, 255, 255, 0.25);
    }

    .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
      background: #07D993;
      color: #000 !important;
    }

    .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
      border-color: #07D993;
    }

    /* 시간 패널 스타일 */
    .ant-picker-time-panel {
      border-left: 1px solid rgba(255, 255, 255, 0.1);
    }

    .ant-picker-time-panel-column {
      border-left: 1px solid rgba(255, 255, 255, 0.1);
      overflow-y: auto !important;
      padding-right: 6px !important; /* 스크롤바 공간 확보 */

      /* 스크롤바 - 컬럼 오른쪽 padding 영역에 위치 */
      &::-webkit-scrollbar {
        width: 4px;
        position: absolute;
        right: 0;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      /* Firefox */
      scrollbar-width: thin;
      scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
    }

    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner {
      color: rgba(255, 255, 255, 0.85);
      text-align: center;
      padding-left: 0 !important;
    }

    /* hover 스타일 */
    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell:hover .ant-picker-time-panel-cell-inner {
      background: rgba(7, 217, 147, 0.2) !important;
      color: rgba(255, 255, 255, 0.85) !important;
    }

    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
      background: #07D993 !important;
      color: #000 !important;
    }

    /* 선택된 셀 hover 시에도 동일하게 유지 */
    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected:hover .ant-picker-time-panel-cell-inner {
      background: #05c083 !important;
      color: #000 !important;
    }

    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-disabled .ant-picker-time-panel-cell-inner {
      color: rgba(255, 255, 255, 0.25);
    }

    /* OK 버튼 영역 */
    .ant-picker-footer {
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .ant-picker-ok button {
      background: #07D993;
      border-color: #07D993;
      color: #000;
    }

    .ant-picker-ok button:hover {
      background: #05c083;
      border-color: #05c083;
    }

    .ant-picker-now-btn {
      color: #07D993;
    }
  }
`;

export default TimeInForceDropdown;
